import type { Graph3DRuntime } from '@packages/graph-3d';
import type { ProcessRuntimeState } from '../types';
import type { ProcessGraphCompileResult } from '../utils/processGraph';

export function buildProcessRuntimeView(
  graph: ProcessGraphCompileResult,
  processStates: Map<string, ProcessRuntimeState>,
  options: {
    includeResults: boolean;
  }
): Graph3DRuntime {
  const nodes: NonNullable<Graph3DRuntime['nodes']> = {};
  const edges: NonNullable<Graph3DRuntime['edges']> = {};

  graph.processIds.forEach(processId => {
    const processNode = graph.processNodeMap.get(processId);
    const state = processStates.get(processId);
    if (!state) {
      return;
    }

    nodes[processId] = {
      status: mapProcessStatus(state.status),
      active: state.status !== 'pending',
    };

    if (processNode?.parentProcessId && state.status !== 'pending') {
      edges[`e_${processNode.parentProcessId}_${processId}`] = {
        active: true,
        color: processNode.themeColor,
      };
    }

    const roleIds = graph.processToRoleIds.get(processId) ?? [];
    roleIds.forEach((roleId, index) => {
      const slot = state.slots[index];
      if (!slot) {
        if (state.status !== 'pending') {
          nodes[roleId] = {
            status: 'pending',
            active: true,
          };
          const roleNode = graph.roleNodeMap.get(roleId);
          if (roleNode) {
            edges[`e_${processId}_${roleId}`] = {
              active: true,
              color: roleNode.themeColor,
            };
          }
        }
        return;
      }

      if (slot.person) {
        nodes[roleId] = {
          status: slot.isWorking ? 'running' : 'active',
          active: true,
          color: slot.isWorking ? '#10b981' : undefined,
          message: slot.person.name,
          detail: slot.role || slot.person.roles[0] || '',
        };
        const roleNode = graph.roleNodeMap.get(roleId);
        if (roleNode) {
          edges[`e_${processId}_${roleId}`] = {
            active: true,
            color: roleNode.themeColor,
          };
        }
        return;
      }

      if (state.status !== 'pending') {
        nodes[roleId] = {
          status: 'pending',
          active: true,
        };
        const roleNode = graph.roleNodeMap.get(roleId);
        if (roleNode) {
          edges[`e_${processId}_${roleId}`] = {
            active: true,
            color: roleNode.themeColor,
          };
        }
      }
    });

    if (!options.includeResults) {
      return;
    }

    const resultId = graph.processToResultId.get(processId);
    if (!resultId) {
      return;
    }

    const fullResult = (state.result || '').trim();
    nodes[resultId] = {
      status: fullResult ? 'success' : 'idle',
      active: !!fullResult,
      color: graph.resultNodeMap.get(resultId)?.themeColor,
      message: formatResultPreview(fullResult),
      detail: fullResult,
    };
    if (fullResult) {
      const resultNode = graph.resultNodeMap.get(resultId);
      if (resultNode) {
        edges[`e_${processId}_${resultId}`] = {
          active: true,
          color: resultNode.themeColor,
        };
      }
    }
  });

  return { nodes, edges };
}

function mapProcessStatus(status: ProcessRuntimeState['status']) {
  if (status === 'running') {
    return 'running' as const;
  }
  if (status === 'completed' || status === 'destroyed') {
    return 'completed' as const;
  }
  if (status === 'initialized' || status === 'structured') {
    return 'active' as const;
  }
  return 'idle' as const;
}

function formatResultPreview(
  text: string,
  maxLines = 3,
  maxUnits = 34
): string {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (!compact) {
    return '—';
  }

  const lines: string[] = [];
  let current = '';
  let currentUnits = 0;
  let consumed = 0;

  for (const char of compact) {
    const units = estimatePreviewCharUnits(char);
    if (current && currentUnits + units > maxUnits) {
      lines.push(current);
      current = char;
      currentUnits = units;
      if (lines.length === maxLines) {
        break;
      }
    } else {
      current += char;
      currentUnits += units;
    }
    consumed += 1;
  }

  if (lines.length < maxLines && current) {
    lines.push(current);
  }

  if (consumed < compact.length && lines.length > 0) {
    lines[lines.length - 1] = appendPreviewEllipsis(
      lines[lines.length - 1],
      maxUnits
    );
  }

  return lines.join('\n');
}

function appendPreviewEllipsis(text: string, maxUnits: number) {
  let result = text;
  while (result && measurePreviewTextUnits(`${result}…`) > maxUnits) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function measurePreviewTextUnits(text: string) {
  let units = 0;
  for (const char of text) {
    units += estimatePreviewCharUnits(char);
  }
  return units;
}

function estimatePreviewCharUnits(char: string) {
  return /[\u4e00-\u9fa5]/.test(char) ? 2 : 1;
}
